import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Job, { initialJob } from "../types/job";
import CurrentWeek from "../types/currentWeek";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";
import JobGroup from "../types/jobGroup";
import AddHoursVM from "../types/addHoursVM";
import AddNoteVM from "../types/addNoteVM";
import JobCount from "../types/jobCount";
import OpenJobList from "../types/openJobList";
import Category from "../types/category";
import CompleteJobList from "../types/completeJobList";
import JobEquipment from "../types/jobEquipment";
import AddEquipmentVM from "../types/addEquipmentVM";
import AddVendorVM from "../types/addVendorVM";
import JobVendor from "../types/jobVendor";
import JobMilestone from "../types/jobMilestone";
import AddMilestoneVM from "../types/addMilestoneVM";
import HighlightWeek from "../types/highlightWeek";
import { AnswerTypes } from "../types/questionVM";
import { parseISO } from "date-fns";

interface JobState {
  job: Job;
  categories: Category[];
  currentWeek: CurrentWeek;
  openJobs: JobGroup[];
  jobSearchResults?: SearchResults;
  jobByTypeSearchResults?: SearchResults;
  projectSearchResults?: SearchResults;
  jobFilter: SearchParams;
  siteJobs: JobGroup[];
  addNoteModal: boolean;
  addHoursModal: boolean;
  jobCounts: JobCount[];
  openJobsList?: OpenJobList;
  completeJobsList?: CompleteJobList;
  temporaryEquipment: JobEquipment[];
  temporaryVendors: JobVendor[];
  temporaryMilestones: JobMilestone[];
  status: "idle" | "loading" | "failed";
  getStatus: "idle" | "loading" | "failed";
  addStatus: "idle" | "loading" | "failed";
  openJobStatus: "idle" | "loading" | "failed";
  closedJobStatus: "idle" | "loading" | "failed";
  searchStatus: "idle" | "loading" | "failed";
  highlightStatus: "idle" | "loading" | "failed";
  milestoneStatus: "idle" | "loading" | "failed";
  jobChildren: JobGroup[];
  weeklyHighlight: HighlightWeek;
  highlightJob: boolean;
  milestoneList: JobMilestone[];
  jobMilestone: JobMilestone;
  jobMilestones: JobMilestone[];
}

const initialState: JobState = {
  job: initialJob,
  jobFilter: defaultParams,
  categories: [],
  currentWeek: {
    sunday: 0,
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
  },
  openJobs: [],
  jobSearchResults: undefined,
  siteJobs: [],
  addNoteModal: false,
  addHoursModal: false,
  jobCounts: [],
  openJobsList: {
    statusJobGroups: [],
  },
  completeJobsList: {
    recentComplete: [],
  },
  temporaryEquipment: [],
  temporaryVendors: [],
  temporaryMilestones: [],
  status: "idle",
  getStatus: "idle",
  addStatus: "idle",
  openJobStatus: "idle",
  closedJobStatus: "idle",
  searchStatus: "idle",
  highlightStatus: "idle",
  milestoneStatus: "idle",
  jobChildren: [],
  weeklyHighlight: {
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
  },
  highlightJob: false,
  milestoneList: [],
  jobMilestone: {
    id: 0,
    title: "",
  },
  jobMilestones: [],
};

export const getJobById = createAsyncThunk<Job, number>(
  "job/get/id",
  async (id: number) => {
    const response = await http.get<Job>(`/job/${id}`);
    return response.data;
  }
);

export const editJob = createAsyncThunk<Job, Job>(
  "job/edit",
  async (job: Job) => {
    const response = await http.put<Job>(`/job/${job.id}`, job, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const deleteJob = createAsyncThunk<Job, number>(
  "job/delete",
  async (id: number) => {
    const response = await http.delete<Job>(`/job/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postJob = createAsyncThunk<Job, Job>(
  "job/post",
  async (job: Job) => {
    const response = await http.post<Job>("/job", job, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchJob = createAsyncThunk<
  SearchResults,
  {
    search: SearchParams;
    start?: Date;
    end?: Date;
    ignoreTable?: boolean;
    project?: boolean;
  }
>("job/search", async ({ search, start, end }) => {
  let qs = getSearchQueryString(search);
  let date = "";
  function isValidDate(d: any) {
    return d instanceof Date && !isNaN(d.getTime());
  }

  date +=
    !!start && isValidDate(start) ? `&startDate=${start.toISOString()}` : "";
  date += !!end && isValidDate(end) ? `&endDate=${end.toISOString()}` : "";

  const response = await http.get<SearchResults>(`/job/search?${qs}${date}`);
  return response.data;
});

export const searchJobByType = createAsyncThunk<SearchResults, SearchParams>(
  "job/type-search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`job/search-by-type?${qs}`);
    return response.data;
  }
);

export const completeJob = createAsyncThunk<Job, number>(
  "job/complete",
  async (id: number) => {
    const response = await http.patch<Job>(`/job/${id}/complete`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const reopenJob = createAsyncThunk<Job, number>(
  "job/reopen",
  async (id: number) => {
    const response = await http.patch<Job>(`/job/${id}/reopen`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const getCurrentWeek = createAsyncThunk<CurrentWeek>(
  "job/get/current-week",
  async () => {
    const response = await http.get<CurrentWeek>("/job/current-week");
    return response.data;
  }
);

export const getOpenJobs = createAsyncThunk<OpenJobList, number>(
  "job/get/open-jobs",
  async (jobTypeId: number) => {
    const response = await http.get<OpenJobList>(
      `/job/open-jobs?jobTypeId=${jobTypeId}`
    );
    return response.data;
  }
);

export const getCompleteJobs = createAsyncThunk<CompleteJobList, number>(
  "/job/recently-closed",
  async (jobTypeId: number) => {
    const response = await http.get<CompleteJobList>(
      `/job/complete-jobs?jobTypeId=${jobTypeId}`
    );
    return response.data;
  }
);

export const getSiteJobs = createAsyncThunk<
  JobGroup[],
  { siteId: number; jobTypeId: number }
>("job/get/site", async ({ siteId, jobTypeId }) => {
  const response = await http.get<JobGroup[]>(
    `/job/site-jobs?siteId=${siteId}&jobTypeId=${jobTypeId}`
  );
  return response.data;
});

export const addJobHours = createAsyncThunk<Job, AddHoursVM>(
  "job/add-hours",
  async (hours: AddHoursVM) => {
    const response = await http.patch<Job>(`/job/add-hours`, hours, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const addJobNote = createAsyncThunk<Job, AddNoteVM>(
  "job/add-note",
  async (note: AddNoteVM) => {
    const response = await http.patch<Job>(`/job/add-note`, note, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const getJobCount = createAsyncThunk<JobCount[]>(
  "job/count",
  async () => {
    const response = await http.get<JobCount[]>("/job/counts");
    return response.data;
  }
);

export const deleteJobHours = createAsyncThunk<Job, number>(
  "job/delete-hours",
  async (id: number) => {
    const response = await http.patch<Job>(
      `/job/delete-hours?jobTimeId=${id}`,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const addJobEquipment = createAsyncThunk<Job, AddEquipmentVM>(
  "job/add-equipment",
  async (equipment: AddEquipmentVM) => {
    const response = await http.patch<Job>(`/job/add-equipment`, equipment);
    return response.data;
  }
);

export const deleteJobEquipment = createAsyncThunk<Job, number>(
  "job/delete-equipment",
  async (id: number) => {
    const response = await http.patch<Job>(
      `/job/delete-equipment?jobEquipmentId=${id}`,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const addJobVendor = createAsyncThunk<Job, AddVendorVM>(
  "job/add-vendor",
  async (vendor: AddVendorVM) => {
    const response = await http.patch<Job>(`/job/add-vendor`, vendor);
    return response.data;
  }
);

export const deleteJobVendor = createAsyncThunk<Job, number>(
  "job/delete-vendor",
  async (id: number) => {
    const response = await http.patch<Job>(
      `/job/delete-vendor?jobVendorId=${id}`,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const addJobMilestone = createAsyncThunk<Job, AddMilestoneVM>(
  "job/add-milestone",
  async (milestone: AddMilestoneVM) => {
    const response = await http.patch<Job>(`/job/add-milestone`, milestone, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const deleteJobMilestone = createAsyncThunk<Job, number>(
  "job/delete-milestone",
  async (id: number) => {
    const response = await http.patch<Job>(
      `/job/delete-milestone?jobMilestoneId=${id}`
    );
    return response.data;
  }
);

export const getJobChildren = createAsyncThunk<JobGroup[], number>(
  "job/job-children",
  async (jobId: number) => {
    const response = await http.get<JobGroup[]>(
      `/job/child-jobs?parentJobId=${jobId}`
    );
    return response.data;
  }
);

export const getWeekHighlights = createAsyncThunk<HighlightWeek>(
  "job/week-highlight",
  async () => {
    const response = await http.get<HighlightWeek>(`/job/week-highlight`);
    return response.data;
  }
);

export const getAllJobMilestones = createAsyncThunk<JobMilestone[]>(
  "job/milestones-all",
  async () => {
    const response = await http.get<JobMilestone[]>(`/job/milestones`);
    return response.data;
  }
);

export const getJobMilestone = createAsyncThunk<JobMilestone, number>(
  "/job/milestone/id",
  async (id: number) => {
    const response = await http.get<JobMilestone>(`/job/milestone/${id}`);
    return response.data;
  }
);

export const editJobMilestone = createAsyncThunk<JobMilestone, JobMilestone>(
  "/job/milestone/edit",
  async (model: JobMilestone) => {
    const response = await http.put<JobMilestone>(
      `/job/milestone/${model.id}`,
      model
    );
    return response.data;
  }
);

export const getJobMilestones = createAsyncThunk<JobMilestone[], number>(
  "/job/get/milestones/",
  async (id: number) => {
    const response = await http.get<JobMilestone[]>(`/job/${id}/milestones`);
    return response.data;
  }
);

const jobSlice = createSlice({
  name: "job",
  initialState,
  reducers: {
    resetJob(state) {
      state.job = { ...initialState.job };
      state.categories = initialState.categories;
      state.temporaryEquipment = [];
      state.temporaryMilestones = [];
      state.temporaryVendors = [];
    },
    updateJob(state, action) {
      state.job = action.payload;
    },
    setCategories(state, action: PayloadAction<Category[]>) {
      state.categories = action.payload;
    },
    setAddNoteModal(state, action) {
      state.addNoteModal = action.payload;
    },
    setAddHoursModal(state, action) {
      state.addHoursModal = action.payload;
    },
    setTemporaryEquipment(state, action) {
      state.temporaryEquipment = action.payload;
    },
    setTemporaryVendors(state, action) {
      state.temporaryVendors = action.payload;
    },
    setTemporaryMilestones(state, action) {
      state.temporaryMilestones = action.payload;
    },
    resetJobChildren(state) {
      state.jobChildren = [];
    },
    setJobFilter(state, action) {
      state.jobFilter = action.payload;
    },
    setHighlightJob(state, action) {
      state.highlightJob = action.payload;
    },
    resetJobMilestone(state) {
      state.jobMilestone = { ...initialState.jobMilestone };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getJobById.pending, (state) => {
        state.getStatus = "loading";
      })
      .addCase(getJobById.fulfilled, (state, action) => {
        state.getStatus = "idle";
        const job = action.payload;
        job.answers.forEach((a) => {
          switch (a.question?.answerType.id) {
            case AnswerTypes.Category:
            case AnswerTypes.Method:
            case AnswerTypes.Source:
            case AnswerTypes.Status:
              if (a.value) {
                a.value = JSON.parse(a.value as string);
              }
              break;
            case AnswerTypes.MultipleChoice:
            case AnswerTypes.Site:
            case AnswerTypes.Job:
              try {
                if (
                  a.value &&
                  typeof JSON.parse(a.value as string) === "object"
                ) {
                  a.value = JSON.parse(a.value as string);
                }
              } catch (error) {
                a.value = a.value as string;
              }
              break;
            case AnswerTypes.WeeklyTask:
            case AnswerTypes.Date:
            case AnswerTypes.Time:
            case AnswerTypes.DateTime:
              if (a.value && a.value !== "null") {
                a.value = parseISO(JSON.parse(a.value as string));
              }
              break;
            case AnswerTypes.DateRange:
              if (a.value && a.value !== "[null,null]") {
                a.value = [
                  JSON.parse(a.value as string)[0] !== null
                    ? parseISO(JSON.parse(a.value as string)[0])
                    : null,
                  JSON.parse(a.value as string)[1] !== null
                    ? parseISO(JSON.parse(a.value as string)[1])
                    : null,
                ];
              } else a.value = [null, null];
              break;
            case AnswerTypes.Checkbox:
            case AnswerTypes.Confidential:
              a.value = a.value === "true";
              break;
            default:
              break;
          }
        });
        state.job = job;
      })
      .addCase(getJobById.rejected, (state) => {
        state.getStatus = "failed";
      })
      .addCase(editJob.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editJob.fulfilled, (state, action) => {
        state.status = "idle";
        // const job = action.payload;
        // job.answers.forEach((a) => {
        //   switch (a.question?.answerType.id) {
        //     case AnswerTypes.MultipleChoice:
        //     case AnswerTypes.Category:
        //     case AnswerTypes.Site:
        //     case AnswerTypes.Method:
        //     case AnswerTypes.Source:
        //     case AnswerTypes.Status:
        //       if (a.value) {
        //         a.value = JSON.parse(a.value as string);
        //       }
        //       break;
        //     case AnswerTypes.WeeklyTask:
        //     case AnswerTypes.Date:
        //     case AnswerTypes.Time:
        //     case AnswerTypes.DateTime:
        //       if (a.value && a.value !== "null") {
        //         a.value = parseISO(JSON.parse(a.value as string));
        //       }
        //       break;
        //     case AnswerTypes.DateRange:
        //       if (a.value && a.value !== "[null,null]") {
        //         a.value = [
        //           parseISO(JSON.parse(a.value as string)[0]),
        //           parseISO(JSON.parse(a.value as string)[1]),
        //         ];
        //       }
        //       break;
        //     default:
        //       break;
        //   }
        // });
        // state.job = job;
      })
      .addCase(editJob.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteJob.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteJob.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteJob.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postJob.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postJob.fulfilled, (state, action) => {
        state.status = "idle";
        state.job = action.payload;
      })
      .addCase(postJob.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchJob.pending, (state) => {
        state.searchStatus = "loading";
      })
      .addCase(searchJob.fulfilled, (state, action) => {
        state.searchStatus = "idle";
        if (!!action.meta.arg.ignoreTable) {
          return;
        } else if (!!action.meta.arg.project) {
          state.projectSearchResults = action.payload;
        } else {
          state.jobSearchResults = action.payload;
        }
      })
      .addCase(searchJob.rejected, (state) => {
        state.searchStatus = "failed";
      })
      .addCase(searchJobByType.pending, (state) => {
        state.searchStatus = "loading";
      })
      .addCase(searchJobByType.fulfilled, (state, action) => {
        state.searchStatus = "idle";
        state.jobByTypeSearchResults = action.payload;
      })
      .addCase(searchJobByType.rejected, (state) => {
        state.searchStatus = "failed";
      })
      .addCase(completeJob.pending, (state) => {
        state.status = "loading";
      })
      .addCase(completeJob.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(completeJob.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(reopenJob.pending, (state) => {
        state.status = "loading";
      })
      .addCase(reopenJob.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(reopenJob.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getCurrentWeek.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCurrentWeek.fulfilled, (state, action) => {
        state.status = "idle";
        state.currentWeek = action.payload;
      })
      .addCase(getCurrentWeek.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getOpenJobs.pending, (state) => {
        state.openJobStatus = "loading";
      })
      .addCase(getOpenJobs.fulfilled, (state, action) => {
        state.openJobStatus = "idle";
        const list = action.payload;

        list.statusJobGroups.forEach((l) => {
          l.jobGroups.forEach((j) => {
            j.answers.forEach((a) => {
              switch (a.question?.answerType.id) {
                case AnswerTypes.Site:
                  try {
                    if (
                      a.value &&
                      typeof JSON.parse(a.value as string) === "object"
                    ) {
                      a.value = JSON.parse(a.value as string);
                    }
                  } catch (error) {
                    a.value = a.value as string;
                  }
                  break;

                default:
                  break;
              }
            });
          });
        });

        state.openJobsList = list;
      })
      .addCase(getOpenJobs.rejected, (state) => {
        state.openJobStatus = "failed";
      })
      .addCase(getCompleteJobs.pending, (state) => {
        state.closedJobStatus = "loading";
      })
      .addCase(getCompleteJobs.fulfilled, (state, action) => {
        state.closedJobStatus = "idle";
        state.completeJobsList = action.payload;
      })
      .addCase(getCompleteJobs.rejected, (state) => {
        state.closedJobStatus = "failed";
      })
      .addCase(getSiteJobs.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSiteJobs.fulfilled, (state, action) => {
        state.status = "idle";
        state.siteJobs = action.payload;
      })
      .addCase(getSiteJobs.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(addJobHours.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addJobHours.fulfilled, (state, action) => {
        state.status = "idle";
        state.job.jobTimes = action.payload.jobTimes;
      })
      .addCase(addJobHours.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(addJobNote.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addJobNote.fulfilled, (state, action) => {
        state.status = "idle";
        state.job.jobNotes = action.payload.jobNotes;
      })
      .addCase(addJobNote.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getJobCount.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getJobCount.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobCounts = action.payload;
      })
      .addCase(getJobCount.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteJobHours.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteJobHours.fulfilled, (state, action) => {
        state.status = "idle";
        state.job.jobTimes = action.payload.jobTimes;
      })
      .addCase(deleteJobHours.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(addJobEquipment.pending, (state) => {
        state.addStatus = "loading";
      })
      .addCase(addJobEquipment.fulfilled, (state, action) => {
        state.addStatus = "idle";
        state.job.jobEquipments = action.payload.jobEquipments;
      })
      .addCase(addJobEquipment.rejected, (state) => {
        state.addStatus = "failed";
      })
      .addCase(deleteJobEquipment.pending, (state) => {
        state.addStatus = "loading";
      })
      .addCase(deleteJobEquipment.fulfilled, (state, action) => {
        state.addStatus = "idle";
        state.job.jobEquipments = action.payload.jobEquipments;
      })
      .addCase(deleteJobEquipment.rejected, (state) => {
        state.addStatus = "failed";
      })
      .addCase(addJobVendor.pending, (state) => {
        state.addStatus = "loading";
      })
      .addCase(addJobVendor.fulfilled, (state, action) => {
        state.addStatus = "idle";
        state.job.jobVendors = action.payload.jobVendors;
      })
      .addCase(addJobVendor.rejected, (state) => {
        state.addStatus = "failed";
      })
      .addCase(deleteJobVendor.pending, (state) => {
        state.addStatus = "loading";
      })
      .addCase(deleteJobVendor.fulfilled, (state, action) => {
        state.addStatus = "idle";
        state.job.jobVendors = action.payload.jobVendors;
      })
      .addCase(deleteJobVendor.rejected, (state) => {
        state.addStatus = "failed";
      })
      .addCase(addJobMilestone.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addJobMilestone.fulfilled, (state, action) => {
        state.status = "idle";
        state.job.jobMilestones = action.payload.jobMilestones;
      })
      .addCase(addJobMilestone.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteJobMilestone.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteJobMilestone.fulfilled, (state, action) => {
        state.status = "idle";
        state.job.jobMilestones = action.payload.jobMilestones;
      })
      .addCase(deleteJobMilestone.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getJobChildren.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getJobChildren.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobChildren = action.payload;
      })
      .addCase(getJobChildren.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getWeekHighlights.pending, (state) => {
        state.highlightStatus = "loading";
      })
      .addCase(getWeekHighlights.fulfilled, (state, action) => {
        state.highlightStatus = "idle";
        state.weeklyHighlight = action.payload;
      })
      .addCase(getWeekHighlights.rejected, (state) => {
        state.highlightStatus = "failed";
      })
      .addCase(getAllJobMilestones.pending, (state) => {
        state.milestoneStatus = "loading";
      })
      .addCase(getAllJobMilestones.fulfilled, (state, action) => {
        state.milestoneStatus = "idle";
        state.milestoneList = action.payload;
      })
      .addCase(getAllJobMilestones.rejected, (state) => {
        state.milestoneStatus = "failed";
      })
      .addCase(getJobMilestone.pending, (state) => {
        state.milestoneStatus = "loading";
      })
      .addCase(getJobMilestone.fulfilled, (state, action) => {
        state.milestoneStatus = "idle";
        state.jobMilestone = action.payload;
      })
      .addCase(getJobMilestone.rejected, (state) => {
        state.milestoneStatus = "failed";
      })
      .addCase(getJobMilestones.pending, (state) => {
        state.milestoneStatus = "loading";
      })
      .addCase(getJobMilestones.fulfilled, (state, action) => {
        state.milestoneStatus = "idle";
        state.jobMilestones = action.payload;
      })
      .addCase(getJobMilestones.rejected, (state) => {
        state.milestoneStatus = "failed";
      })
      .addCase(editJobMilestone.pending, (state) => {
        state.milestoneStatus = "loading";
      })
      .addCase(editJobMilestone.fulfilled, (state) => {
        state.milestoneStatus = "idle";
      })
      .addCase(editJobMilestone.rejected, (state) => {
        state.milestoneStatus = "failed";
      });
  },
});

export default jobSlice.reducer;
export const {
  resetJob,
  updateJob,
  setCategories,
  setAddNoteModal,
  setAddHoursModal,
  setTemporaryEquipment,
  setTemporaryVendors,
  setTemporaryMilestones,
  resetJobChildren,
  setJobFilter,
  setHighlightJob,
  resetJobMilestone,
} = jobSlice.actions;
